"use client";

import { motion } from "motion/react";
import {
  Brain,
  Sparkles,
  BookOpen,
  Calculator,
  Globe,
  Microscope,
  PenTool,
  MessageSquare,
  ChevronRight,
  Search,
  Lightbulb,
  Zap,
  BookMarked,
  GraduationCap,
  Bot,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { fadeIn } from "@/lib/framer";

export default function AIPage() {
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const services = [
    {
      title: "AI Chat Assistant",
      description:
        "Get personalized learning support through our intelligent chat interface",
      icon: <Bot className="h-10 w-10 text-primary" />,
      color: "bg-secondary/30",
      link: "/ai/chat",
      features: [
        "Ask questions on any subject",
        "Get instant explanations",
        "Personalized learning support",
      ],
    },
    {
      title: "Homework Helper",
      description:
        "Step-by-step guidance for assignments and homework challenges",
      icon: <Sparkles className="h-10 w-10 text-primary" />,
      color: "bg-accent/30",
      link: "/ai/homework-helper",
      features: [
        "Detailed problem solving",
        "Step-by-step explanations",
        "Multiple subject support",
      ],
    },
    {
      title: "Research Assistant",
      description: "Find reliable information and resources for your projects",
      icon: <Search className="h-10 w-10 text-primary" />,
      color: "bg-muted/30",
      link: "/ai/research-assistant",
      features: [
        "Curated educational resources",
        "Citation assistance",
        "Reliable academic sources",
      ],
    },
  ];
  return (
    <main className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-indigo-950">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 dark:from-blue-900/20 dark:to-indigo-900/20"></div>
          <ParticleBackground />
        </div>

        <div className="container relative z-10 px-4 py-24 mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center px-4 py-2 mb-6 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-full dark:bg-indigo-900/50 dark:text-indigo-300">
              <Sparkles className="w-4 h-4 mr-2" />
              <span>Introducing AI-Powered Learning</span>
            </div>

            <h1 className="mb-6 text-5xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-400 dark:to-blue-400 md:text-6xl lg:text-7xl">
              Empower Your Learning Journey with AI
            </h1>

            <p className="mb-10 text-xl text-gray-600 dark:text-gray-300">
              Discover how artificial intelligence can enhance your educational
              experience, help you solve complex problems, and unlock your full
              potential.
            </p>

            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button
                size="lg"
                className="gap-2 text-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
              >
                <Link href="#services" className="flex items-center gap-2 ">
                  Try AI Assistant <MessageSquare className="w-5 h-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="gap-2 text-lg">
                <Link href="#how-it-works" className="flex items-center gap-2">
                  Learn How It Works <ChevronRight className="w-5 h-5" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Floating 3D elements */}
        <div className="absolute top-1/4 left-10 z-0 opacity-70 dark:opacity-40">
          <FloatingElement
            icon={<Brain className="w-full h-full text-indigo-500" />}
            size={80}
            duration={8}
          />
        </div>
        <div className="absolute bottom-1/4 right-10 z-0 opacity-70 dark:opacity-40">
          <FloatingElement
            icon={<Lightbulb className="w-full h-full text-primary" />}
            size={60}
            duration={10}
          />
        </div>
        <div className="absolute top-2/3 left-1/4 z-0 opacity-70 dark:opacity-40">
          <FloatingElement
            icon={<BookOpen className="w-full h-full text-indigo-400" />}
            size={50}
            duration={12}
          />
        </div>
      </section>

      {/* AI Features Section */}
      <section className="py-20">
        <div className="container px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto mb-16 text-center"
          >
            <h2 className="mb-6 text-4xl font-bold text-gray-900 dark:text-white">
              AI Tools for Every Subject
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Our AI platform offers specialized tools designed to help you
              excel in every subject area.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {aiFeatures.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Interactive AI Demo */}
      <motion.div
        id="services"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="max-w-3xl mx-auto mb-16 text-center"
      >
        <h2 className="mb-6 text-4xl font-bold text-gray-900 dark:text-white">
          Experience AI in Action
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-300">
          Try our interactive AI tools and see how they can help with your
          studies.
        </p>
      </motion.div>

      <motion.div
        variants={staggerContainer}
        initial="visible"
        animate="visible"
        className="grid container mx-auto grid-cols-1 md:grid-cols-3 gap-8 mb-16"
      >
        {services.map((service, index) => (
          <motion.div
            key={index}
            variants={fadeIn}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Link href={service.link} className="block h-full">
              <Card className="h-full hover:shadow-lg transition-shadow">
                <CardHeader className={`${service.color} rounded-t-lg -mt-6`}>
                  <div className="flex justify-center mb-4 mt-2">
                    {service.icon}
                  </div>
                  <CardTitle className="text-center">{service.title}</CardTitle>
                  <CardDescription className="text-center mb-4">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center">
                        <div className="h-2 w-2 rounded-full bg-primary mr-2"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button className="w-full">
                    <span>Explore</span>
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardFooter>
              </Card>
            </Link>
          </motion.div>
        ))}
      </motion.div>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-b from-indigo-50 to-white dark:from-gray-900 dark:to-gray-950">
        <div className="container px-4 mx-auto">
          <motion.div
            id="how-it-works"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto mb-16 text-center"
          >
            <h2 className="mb-6 text-4xl font-bold text-gray-900 dark:text-white">
              How Our AI Learning Works
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Discover the process behind our AI-powered educational tools.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 -translate-x-1/2 h-full w-1 bg-indigo-200 dark:bg-indigo-900/50"></div>

            {howItWorks.map((step, index) => (
              <TimelineStep key={index} step={step} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Student Success Stories */}
      <section className="py-20 bg-background">
        <div className="container px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto mb-16 text-center"
          >
            <h2 className="mb-6 text-4xl font-bold text-gray-900 dark:text-white">
              Student Success Stories
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              See how our AI tools have helped students achieve their academic
              goals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                testimonial={testimonial}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Get Started CTA */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-900 dark:to-blue-900">
        <div className="container px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl p-8 mx-auto text-center bg-card rounded-2xl shadow-xl"
          >
            <h2 className="mb-6 text-4xl font-bold text-gray-900 dark:text-white">
              Ready to Transform Your Learning?
            </h2>
            <p className="mb-8 text-xl text-gray-600 dark:text-gray-300">
              Join thousands of students who are already using our AI tools to
              excel in their studies.
            </p>
            <Button
              size="lg"
              className="gap-2 text-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
            >
              Get Started Today <Zap className="w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

// Particle Background Component
function ParticleBackground() {
  return (
    <div className="absolute inset-0">
      {Array.from({ length: 50 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 bg-indigo-500 rounded-full opacity-20 dark:opacity-30"
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, Math.random() * 30 - 15],
            x: [0, Math.random() * 30 - 15],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 5 + Math.random() * 10,
            repeat: Number.POSITIVE_INFINITY,
            repeatType: "reverse",
          }}
        />
      ))}
    </div>
  );
}

// Floating Element Component
function FloatingElement({
  icon,
  size,
  duration,
}: {
  icon: React.ReactNode;
  size: number;
  duration: number;
}) {
  return (
    <motion.div
      className="relative"
      style={{ width: size, height: size }}
      animate={{
        y: [0, -20, 0],
        rotate: [0, 5, -5, 0],
      }}
      transition={{
        duration,
        repeat: Number.POSITIVE_INFINITY,
        repeatType: "loop",
        ease: "easeInOut",
      }}
    >
      {icon}
    </motion.div>
  );
}

// Feature Card Component
function FeatureCard({
  feature,
  index,
}: {
  feature: {
    icon: React.ReactNode;
    title: string;
    description: string;
    tags: string[];
  };
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <Card className="overflow-hidden h-full">
        <div className="p-6">
          <div className="flex items-center justify-center w-12 h-12 mb-4 rounded-lg bg-indigo-100 dark:bg-indigo-900/50">
            {feature.icon}
          </div>
          <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">
            {feature.title}
          </h3>
          <p className="text-gray-600 dark:text-gray-300">
            {feature.description}
          </p>

          <div className="flex flex-wrap gap-2 mt-4">
            {feature.tags.map((tag, i) => (
              <span
                key={i}
                className="px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 rounded-full dark:bg-indigo-900/50 dark:text-indigo-300"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

// Timeline Step Component
function TimelineStep({
  step,
  index,
}: {
  step: { icon: React.ReactNode; title: string; description: string };
  index: number;
}) {
  const isEven = index % 2 === 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: isEven ? -20 : 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      className={`relative mb-12 ${isEven ? "pr-8 md:pr-0 md:mr-auto md:ml-0 md:pl-0" : "pl-8 md:pl-0 md:ml-auto md:mr-0 md:pr-0"} md:w-5/12`}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-8 bg-indigo-500 dark:bg-indigo-600 rounded-full z-10 flex items-center justify-center text-white font-bold">
        {index + 1}
      </div>

      <Card className="p-6 border-none shadow-lg">
        <div className="flex items-center mb-4">
          <div className="p-2 mr-4 bg-indigo-100 rounded-lg dark:bg-indigo-900/50">
            {step.icon}
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {step.title}
          </h3>
        </div>
        <p className="text-gray-600 dark:text-gray-300">{step.description}</p>
      </Card>
    </motion.div>
  );
}

// Testimonial Card Component
function TestimonialCard({
  testimonial,
  index,
}: {
  testimonial: {
    name: string;
    grade: string;
    quote: string;
    stars: number;
    subject: string;
  };
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
    >
      <Card className="h-full p-6 overflow-hidden border-none shadow-lg">
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 mr-4 overflow-hidden rounded-full bg-gradient-to-r from-indigo-400 to-blue-400">
            <Image
              src={`/placeholder.svg?height=48&width=48&text=${testimonial.name.charAt(0)}`}
              alt={testimonial.name}
              className="object-cover w-full h-full"
              width={48}
              height={48}
            />
          </div>
          <div>
            <h4 className="text-lg font-bold text-gray-900 dark:text-white">
              {testimonial.name}
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {testimonial.grade}
            </p>
          </div>
        </div>

        <p className="mb-4 italic text-gray-600 dark:text-gray-300">
          {testimonial.quote}
        </p>

        <div className="flex items-center mt-auto">
          <div className="flex">
            {Array.from({ length: 5 }).map((_, i) => (
              <Sparkles
                key={i}
                className={`w-4 h-4 ${i < testimonial.stars ? "text-yellow-500" : "text-gray-300 dark:text-gray-600"}`}
              />
            ))}
          </div>
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
            {testimonial.subject}
          </span>
        </div>
      </Card>
    </motion.div>
  );
}

// Data
const aiFeatures = [
  {
    title: "Math Mastery",
    description:
      "Get step-by-step solutions to math problems from basic arithmetic to advanced algebra and geometry.",
    icon: <Calculator className="w-6 h-6 text-indigo-600" />,
    tags: ["Problem Solving", "Visual Explanations", "Practice Problems"],
  },
  {
    title: "Language Arts",
    description:
      "Improve your writing skills with grammar checks, vocabulary suggestions, and creative writing prompts.",
    icon: <PenTool className="w-6 h-6 text-indigo-600" />,
    tags: ["Grammar", "Vocabulary", "Essay Structure"],
  },
  {
    title: "Science Explorer",
    description:
      "Visualize scientific concepts, conduct virtual experiments, and explore interactive models.",
    icon: <Microscope className="w-6 h-6 text-indigo-600" />,
    tags: ["Interactive Models", "Experiments", "Visual Learning"],
  },
  {
    title: "Social Studies",
    description:
      "Travel through time with interactive historical timelines, maps, and cultural explorations.",
    icon: <Globe className="w-6 h-6 text-indigo-600" />,
    tags: ["History", "Geography", "Cultural Studies"],
  },
  {
    title: "Study Assistant",
    description:
      "Create personalized study plans, flashcards, and quizzes to help you prepare for exams.",
    icon: <BookMarked className="w-6 h-6 text-indigo-600" />,
    tags: ["Study Plans", "Flashcards", "Practice Tests"],
  },
  {
    title: "Research Helper",
    description:
      "Find age-appropriate sources, summarize information, and organize your research projects.",
    icon: <Search className="w-6 h-6 text-indigo-600" />,
    tags: ["Source Finding", "Summarization", "Organization"],
  },
];

const howItWorks = [
  {
    title: "Personalized Learning",
    description:
      "Our AI analyzes your learning style, strengths, and areas for improvement to create a personalized educational experience.",
    icon: <GraduationCap className="w-6 h-6 text-indigo-600" />,
  },
  {
    title: "Interactive Problem Solving",
    description:
      "Rather than just giving answers, our AI guides you through the problem-solving process, helping you understand concepts deeply.",
    icon: <Lightbulb className="w-6 h-6 text-indigo-600" />,
  },
  {
    title: "Adaptive Feedback",
    description:
      "Receive instant, constructive feedback on your work that adapts to your progress and helps you improve continuously.",
    icon: <MessageSquare className="w-6 h-6 text-indigo-600" />,
  },
  {
    title: "Resource Curation",
    description:
      "Access a curated collection of educational resources, examples, and practice materials tailored to your learning needs.",
    icon: <BookOpen className="w-6 h-6 text-indigo-600" />,
  },
  {
    title: "Progress Tracking",
    description:
      "Monitor your academic growth with detailed progress tracking and achievement milestones to celebrate your success.",
    icon: <Zap className="w-6 h-6 text-indigo-600" />,
  },
];

const testimonials = [
  {
    name: "Aarav Sharma",
    grade: "Grade 5 Student",
    quote:
      "The AI math helper explains problems in a way that's easy to understand. I used to struggle with fractions, but now I get them!",
    stars: 5,
    subject: "Mathematics",
  },
  {
    name: "Priya Patel",
    grade: "Grade 6 Student",
    quote:
      "I love using the science explorer to see 3D models of cells and planets. It makes learning science so much more fun!",
    stars: 5,
    subject: "Science",
  },
  {
    name: "Rohan Gupta",
    grade: "Grade 4 Student",
    quote:
      "The writing assistant helped me organize my thoughts for my essay. My teacher was really impressed with my improvement!",
    stars: 4,
    subject: "Language Arts",
  },
];
